import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaApple, FaGlobe, FaLinux, FaWindows } from 'react-icons/fa';
import { SiAndroid, SiFirefoxbrowser, SiGooglechrome } from 'react-icons/si';
import {
    FiBookmark,
    FiCheckSquare,
    FiFileText,
    FiFolder,
    FiGrid,
    FiImage,
    FiLock,
    FiMessageSquare,
    FiMic,
    FiSearch,
    FiSettings,
    FiShare2,
    FiSmartphone,
    FiUsers,
    FiZap
} from 'react-icons/fi';
import FiIcons from '../fiIconMap';
import LayoutDefault from '../layouts/LayoutDefault';
import '../assets/scss/style.scss';
import { COMPETITOR_ICONS } from '../data/competitorIcons';
import { preloadMarketingPages, getLoadedMarketingPages } from '../../../data/marketingPagesLoader';
import { MARKETING_PAGE_META } from '../../../data/marketingPageMeta';
import AuthorByline from '../components/elements/AuthorByline';
import { getTestimonialsByIds } from '../../../data/testimonials';

import marketingHeroApp from '../assets/images/marketing-hero-app.png';
import marketingHeroBrowserExtension from '../assets/images/marketing-hero-browser-extension.png';
import marketingHeroGoogleKeep from '../assets/images/marketing-hero-google-keep.png';
import luckyIcon from '../../../assets/lucky_icon.svg';
import competitorPricingModule from '../../../data/competitorPricing';

const { COMPETITOR_PRICING } = competitorPricingModule;

// Light-page palette for each competitor's icon chip (hero eyebrow row).
const COMPETITOR_ICON_STYLE = {
    'google-keep': { color: '#b45309', background: 'rgba(255, 187, 0, 0.15)' },
    'apple-notes': { color: '#1a1a1a', background: 'rgba(0, 0, 0, 0.06)' },
    pocket: { color: '#EF3F56', background: 'rgba(239, 63, 86, 0.12)' },
    evernote: { color: '#00A82D', background: 'rgba(0, 168, 45, 0.12)' },
    fabric: { background: 'rgba(0, 0, 0, 0.06)' },
    mymind: { background: 'rgba(0, 0, 0, 0.06)' },
    glasp: { background: 'rgba(0, 0, 0, 0.06)' },
    notion: { color: '#1a1a1a', background: 'rgba(0, 0, 0, 0.06)' },
    obsidian: { color: '#7c3aed', background: 'rgba(124, 58, 237, 0.12)' },
    capacities: { color: '#0ea5e9', background: 'rgba(14, 165, 233, 0.12)' },
    dropbox: { color: '#0061ff', background: 'rgba(0, 97, 255, 0.12)' },
    'google-drive': { color: '#1a73e8', background: 'rgba(26, 115, 232, 0.12)' },
    pinterest: { color: '#e60023', background: 'rgba(230, 0, 35, 0.12)' },
    raindrop: { color: '#3a97d3', background: 'rgba(58, 151, 211, 0.12)' },
    otter: { color: '#00b2b2', background: 'rgba(0, 178, 178, 0.12)' },
    voicenotes: { color: '#f97316', background: 'rgba(249, 115, 22, 0.12)' }
};

const IMAGE_MAP = {
    'marketing-hero-app': marketingHeroApp,
    'marketing-hero-browser-extension': marketingHeroBrowserExtension,
    'marketing-hero-google-keep': marketingHeroGoogleKeep,
};

const DOWNLOAD_ICON_MAP = {
    chrome: {
        component: SiGooglechrome,
        color: '#4285F4',
        background: 'rgba(66, 133, 244, 0.10)'
    },
    firefox: {
        component: SiFirefoxbrowser,
        color: '#FF7139',
        background: 'rgba(255, 113, 57, 0.10)'
    },
    apple: {
        component: FaApple,
        color: '#111827',
        background: 'rgba(17, 24, 39, 0.08)'
    },
    ios: {
        component: FaApple,
        color: '#111827',
        background: 'rgba(17, 24, 39, 0.08)'
    },
    android: {
        component: SiAndroid,
        color: '#3DDC84',
        background: 'rgba(61, 220, 132, 0.12)'
    },
    windows: {
        component: FaWindows,
        color: '#0078D4',
        background: 'rgba(0, 120, 212, 0.10)'
    },
    linux: {
        component: FaLinux,
        color: '#111827',
        background: 'rgba(17, 24, 39, 0.08)'
    },
    web: {
        component: FaGlobe,
        color: '#2b88d9',
        background: 'rgba(43, 136, 217, 0.10)'
    }
};

async function downloadLatestDesktopArtifact(platformKey, urlKey) {
    try {
        const response = await fetch('https://luckynote.s3.eu-central-1.amazonaws.com/releases/desktop/stable/latest.json', {
            cache: 'no-store'
        });
        if (!response.ok) {
            throw new Error(`Manifest request failed: ${response.status}`);
        }

        const latest = await response.json();
        const artifactUrl = latest?.platforms?.[platformKey]?.[urlKey];
        if (!artifactUrl) {
            throw new Error(`No ${urlKey} found for ${platformKey}`);
        }

        window.location.href = artifactUrl;
    } catch (error) {
        console.warn('[desktop download] Could not resolve latest desktop artifact:', error);
        window.alert('The latest desktop download is not available yet. Please try again soon.');
    }
}

const DEFAULT_CTA = {
    loggedOutLabel: 'Start free',
    loggedOutTo: '/register',
    loggedInLabel: 'Open Luckynote',
    loggedInTo: '/'
};

function resolveAction(action, hasToken) {
    if (!action) {
        return null;
    }

    const label = hasToken ? action.loggedInLabel || action.loggedOutLabel : action.loggedOutLabel;
    const to = hasToken ? action.loggedInTo || action.loggedOutTo : action.loggedOutTo;
    const href = hasToken ? action.loggedInHref || action.loggedOutHref : action.loggedOutHref;
    const sectionId = hasToken ? action.loggedInSectionId || action.loggedOutSectionId : action.loggedOutSectionId;

    if (href) {
        return {
            label,
            href,
            external: true
        };
    }

    if (!to) {
        if (!sectionId) {
            return null;
        }

        return {
            label,
            sectionId,
            external: false,
            inPage: true
        };
    }

    return {
        label,
        to,
        external: false,
        sectionId,
        inPage: false
    };
}

function ActionButton({ action, secondary }) {
    if (!action) {
        return null;
    }

    if (action.external) {
        return (
            <ActionAnchor
                href={action.href}
                target="_blank"
                rel="noopener noreferrer"
                $secondary={secondary}
            >
                {action.label}
            </ActionAnchor>
        );
    }

    if (action.inPage) {
        return (
            <ActionAnchor
                href={`#${action.sectionId}`}
                onClick={(event) => {
                    event.preventDefault();
                    const element = document.getElementById(action.sectionId);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }}
                $secondary={secondary}
            >
                {action.label}
            </ActionAnchor>
        );
    }

    return (
        <ActionLink to={action.to} $secondary={secondary}>
            {action.label}
        </ActionLink>
    );
}

function DownloadAction({ item, secondary = false, compact = false }) {
    const content = (
        <span>{item.label}</span>
    );

    if (item.platformKey) {
        return (
            <DownloadButton
                type="button"
                $secondary={secondary}
                $compact={compact}
                onClick={() => downloadLatestDesktopArtifact(item.platformKey, item.urlKey || 'download_url')}
            >
                {content}
            </DownloadButton>
        );
    }

    if (item.href) {
        return (
            <DownloadButtonAnchor
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                $secondary={secondary}
                $compact={compact}
            >
                {content}
            </DownloadButtonAnchor>
        );
    }

    return (
        <DownloadButtonDisabled $compact={compact} aria-disabled="true">
            {content}
        </DownloadButtonDisabled>
    );
}

function formatPathLabel(path) {
    const slug = path.split('/').filter(Boolean).pop() || '';
    return slug
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function formatKindLabel(kind) {
    const labels = {
        comparison: 'Comparison',
        guide: 'Guide',
        use_case: 'Use case',
        message_yourself: 'Message yourself',
        persona: 'Persona'
    };

    return labels[kind] || formatPathLabel(kind || '');
}

function getCompetitorDisplayName(page) {
    if (!page?.title) {
        return 'Other app';
    }

    return page.title
        .replace(/\s+Alternatives?\s*\|\s*Luckynote$/i, '')
        .replace(/\s+Alternative\s*\|\s*Luckynote$/i, '')
        .trim();
}

function getVersusColumnIdentity(section, side, page, competitorDisplayName) {
    const column = section[side];
    const app = column.app || (side === 'left' ? 'luckynote' : page.competitorIcon);

    return {
        app,
        name: column.name || (side === 'left' ? 'Luckynote' : competitorDisplayName)
    };
}

function renderVersusLogo(app) {
    if (app === 'luckynote') {
        return <VersusLogo src={luckyIcon} alt="" width={22} height={22} />;
    }

    const icon = COMPETITOR_ICONS[app];
    if (!icon) {
        return null;
    }

    return (
        <VersusLogoMark $color={COMPETITOR_ICON_STYLE[app]?.color}>
            {icon.component
                ? React.createElement(icon.component, { size: 18 })
                : icon.image
                    ? <img src={icon.image} alt="" width={22} height={22} />
                    : <TableHeaderMonogram>{icon.letter}</TableHeaderMonogram>}
        </VersusLogoMark>
    );
}

function getStrengthIconCandidates(item) {
    const content = String(item).toLowerCase();
    const candidates = [];
    const add = (variant) => {
        if (!candidates.includes(variant)) {
            candidates.push(variant);
        }
    };

    if (/(search|retriev|find|ocr|ai-assisted)/.test(content)) add('search');
    if (/(task|reminder|follow[- ]?up|action)/.test(content)) add('checklist');
    if (/(capture|save|message|inbox|quick)/.test(content)) add('message');
    if (/(voice|spoken|transcri)/.test(content)) add('voice');
    if (/(screenshot|image|visual|moodboard|photo|browse)/.test(content)) add('image');
    if (/(collaborat|share)/.test(content)) add('share');
    if (/(team|multi-user|people)/.test(content)) add('people');
    if (/(adapt|configur|process|workflow)/.test(content)) add('settings');
    if (/(database|workspace|wiki|template|system|structure|object|model)/.test(content)) add('layers');
    if (/(folder|tag|collection|organize|archive|library)/.test(content)) add('folder');
    if (/(file|document|markdown|pdf)/.test(content)) add('file');
    if (/(mobile|phone)/.test(content)) add('mobile');
    if (/(private|privacy|local|ownership|security)/.test(content)) add('lock');
    if (/(link|web|article|bookmark|read[- ]?later|page)/.test(content)) add('bookmark');

    return candidates;
}

function getStrengthIconVariants(items) {
    const usedVariants = new Set();
    const fallbacks = ['zap', 'search', 'bookmark', 'layers', 'checklist', 'message', 'file', 'folder'];

    return items.map((item) => {
        const candidates = getStrengthIconCandidates(item);
        const variant = candidates.find((candidate) => !usedVariants.has(candidate))
            || fallbacks.find((fallback) => !usedVariants.has(fallback))
            || candidates[0]
            || 'search';

        usedVariants.add(variant);
        return variant;
    });
}

function StrengthIcon({ variant = 'search' }) {
    const iconMap = {
        search: FiSearch,
        bookmark: FiBookmark,
        bolt: FiZap,
        file: FiFileText,
        folder: FiFolder,
        image: FiImage,
        layers: FiGrid,
        checklist: FiCheckSquare,
        lock: FiLock,
        message: FiMessageSquare,
        mobile: FiSmartphone,
        people: FiUsers,
        settings: FiSettings,
        share: FiShare2,
        voice: FiMic,
        zap: FiZap
    };

    const IconComponent = iconMap[variant] || FiSearch;
    return <IconComponent aria-hidden="true" />;
}

// Cross-page link chips only need path/kind/icon/eyebrow metadata, which
// lives in the small generated marketingPageMeta.js module - no need to
// wait for the full content chunk for these.
function getMarketingLinkLabel(link) {
    const targetPage = MARKETING_PAGE_META.find((candidate) => candidate.path === link.to);
    if (!targetPage) {
        return link.label;
    }

    if (targetPage.kind === 'persona' && targetPage.heroEyebrow) {
        return targetPage.heroEyebrow.replace(/^For\s+/i, '');
    }

    return link.label || formatPathLabel(targetPage.path);
}

function getPageChipMeta(page) {
    return {
        color: page?.iconColor || '#111827',
        background: page?.iconBackground || 'rgba(17, 24, 39, 0.08)'
    };
}

function getMarketingLinkMeta(link) {
    const targetPage = MARKETING_PAGE_META.find((candidate) => candidate.path === link.to);
    return {
        to: link.to,
        label: getMarketingLinkLabel(link),
        icon: targetPage?.icon,
        iconColor: targetPage?.iconColor,
        iconBackground: targetPage?.iconBackground
    };
}

function formatDate(isoDate) {
    if (!isoDate) {
        return null;
    }

    const [year, month, day] = isoDate.split('-').map((value) => Number(value));
    const date = new Date(Date.UTC(year, month - 1, day));

    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC'
    }).format(date);
}

function getComparisonPricing(path) {
    const match = path.match(/^\/compare\/(.+)-alternative$/);
    if (!match) {
        return null;
    }

    return COMPETITOR_PRICING[match[1]] || null;
}

function buildExploreLinks(kind, currentPath) {
    return MARKETING_PAGE_META
        .filter((candidate) => candidate.kind === kind && candidate.path !== currentPath)
        .slice(0, 8)
        .map((candidate) => ({
            to: candidate.path,
            icon: candidate.icon,
            iconColor: candidate.iconColor,
            iconBackground: candidate.iconBackground,
            label:
                kind === 'persona' && candidate.heroEyebrow
                    ? candidate.heroEyebrow.replace(/^For\s+/i, '')
                    : formatPathLabel(candidate.path)
        }));
}

const renderAppIcon = (appName, size = 18) => {
    const raw = (appName || '').trim();
    const key = raw.toLowerCase();
    if (key.includes('lucky')) {
        return <img src={luckyIcon} alt="Luckynote" style={{ width: size, height: size, objectFit: 'contain' }} />;
    }

    const iconKey = key.includes('notion')
        ? 'notion'
        : key.includes('obsidian')
            ? 'obsidian'
            : key.includes('capacit')
                ? 'capacities'
                : key.includes('evernote')
                    ? 'evernote'
                    : key.includes('keep')
                        ? 'google-keep'
                        : key.includes('apple')
                            ? 'apple-notes'
                            : key.includes('raindrop')
                                ? 'raindrop'
                                : key.includes('mymind')
                                    ? 'mymind'
                                    : key.includes('glasp')
                                        ? 'glasp'
                                        : key.includes('fabric')
                                            ? 'fabric'
                                            : key.includes('dropbox')
                                                ? 'dropbox'
                                                : key.includes('drive')
                                                    ? 'google-drive'
                                                    : key.includes('otter')
                                                        ? 'otter'
                                                        : key.includes('voicenotes')
                                                            ? 'voicenotes'
                                                            : null;

    if (iconKey && COMPETITOR_ICONS[iconKey]) {
        const item = COMPETITOR_ICONS[iconKey];
        if (item.component) {
            return React.createElement(item.component, { size });
        }
        if (item.image) {
            return <img src={item.image} alt={raw} style={{ width: size, height: size, objectFit: 'contain' }} />;
        }
    }

    return <span style={{ fontSize: `${Math.round(size * 0.7)}px`, fontWeight: 800 }}>{raw.charAt(0)}</span>;
};

// The full page content lives in the large marketingPages.js data module,
// which is code-split out of the main bundle (see marketingPagesLoader.js).
// Direct loads of a marketing route preload that chunk in index.js BEFORE
// React mounts, so the first render here is synchronous and matches the
// react-snap prerendered HTML exactly (no hydration mismatch, no flash of
// empty content). Only client-side navigations with a cold cache hit the
// async loading branch, where nothing prerendered is on screen anyway.
const MarketingPage = ({ path }) => {
    const [marketingData, setMarketingData] = React.useState(getLoadedMarketingPages);

    React.useEffect(() => {
        if (marketingData) {
            return undefined;
        }
        let cancelled = false;
        preloadMarketingPages().then((mod) => {
            if (!cancelled) {
                setMarketingData(mod);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [marketingData]);

    if (!marketingData) {
        return <LayoutDefault><PageShell /></LayoutDefault>;
    }

    const page = marketingData.getMarketingPageByPath(path);

    if (!page) {
        return <LayoutDefault><PageShell /></LayoutDefault>;
    }

    const hasToken =
        typeof window !== 'undefined' &&
        typeof window.localStorage !== 'undefined' &&
        Boolean(window.localStorage.getItem('token'));

    const primaryAction = resolveAction(page.primaryCta || DEFAULT_CTA, hasToken);
    const secondaryAction = resolveAction(page.secondaryCta, hasToken);
    const isEditorial = Boolean(page.editorial || page.editorialPersona);
    const isEditorialPersona = Boolean(page.editorialPersona);
    const isPersonaPage = page.kind === 'persona';
    const shouldHideUseCaseHeroImage = page.kind === 'use_case' || page.kind === 'message_yourself' || page.kind === 'guide';
    const heroHasImage =
        (!shouldHideUseCaseHeroImage && !!page.hero.image) ||
        (!shouldHideUseCaseHeroImage && !!page.hero.imagePlaceholder);
    const HeroIconComponent = page.icon ? FiIcons[page.icon] : null;
    const heroChipMeta = getPageChipMeta(page);
    const heroAlign =
        page.editorial
            ? 'left'
            : page.hero.align ||
            (!heroHasImage && (page.competitorIcon || page.kind === 'use_case' || page.kind === 'message_yourself' || page.kind === 'guide') ? 'center' : undefined);
    const showCrossLinks =
        page.kind === 'persona' ||
        page.kind === 'use_case' ||
        page.kind === 'message_yourself' ||
        page.kind === 'guide' ||
        page.kind === 'comparison';
    const similarHeading =
        page.kind === 'persona'
            ? 'Similar pages:'
            : page.kind === 'message_yourself'
                ? 'Related message-yourself pages:'
                : page.kind === 'guide'
                    ? 'Related guides:'
                : page.kind === 'comparison'
                    ? 'Related pages:'
                    : 'Similar use cases:';
    const exploreHeading =
        page.kind === 'persona'
            ? 'Explore other personas:'
            : page.kind === 'message_yourself'
                ? 'Explore other message-yourself apps:'
                : page.kind === 'guide'
                    ? 'Explore other guides:'
                : page.kind === 'comparison'
                    ? 'Explore other comparisons:'
                    : 'Explore other use cases:';
    const similarLinks = (page.relatedLinks || []).map((link) => getMarketingLinkMeta(link));
    const exploreLinks = page.kind === 'comparison' ? [] : buildExploreLinks(page.kind, page.path);
    const comparisonPricing = getComparisonPricing(page.path);
    const editorialCategoryLabel = page.editorial ? formatKindLabel(page.kind) : undefined;
    const competitorDisplayName = getCompetitorDisplayName(page);

    // Comparison pages have no hero image, so they read best as a single
    // centered column (matching how strong "X vs Y" pages in the wild lay
    // this out) rather than inheriting the default left-aligned layout that
    // only makes sense next to a side image.

    return (
        <LayoutDefault>
            <PageShell $editorial={isEditorial} $compact={page.compactReading}>
                <HeroSection
                    $hasImage={heroHasImage}
                    $align={heroAlign}
                    $hideBorder={page.hideFinalCta}
                    $editorial={isEditorial}
                >
                    <HeroContent
                        $hasImage={heroHasImage}
                        $align={heroAlign}
                        $editorial={isEditorial}
                    >
                        {page.editorial ? (
                            <EditorialBreadcrumb>
                                <span>Blog</span>
                                <span aria-hidden="true">·</span>
                                <span>{editorialCategoryLabel}</span>
                            </EditorialBreadcrumb>
                        ) : null}
                        {page.compareApps && page.compareApps.length > 0 ? (
                            <HeroAppsCluster>
                                <HeroClusterItem $featured title="Luckynote">
                                    <HeroClusterIconWrap $background="rgba(43, 136, 217, 0.12)">
                                        <img src={luckyIcon} alt="Luckynote" />
                                    </HeroClusterIconWrap>
                                    <span>Luckynote</span>
                                </HeroClusterItem>
                                <HeroClusterVs>vs</HeroClusterVs>
                                {page.compareApps.filter(appKey => appKey !== 'luckynote').map(appKey => (
                                    <HeroClusterItem key={appKey} title={COMPETITOR_PRICING[appKey]?.name || appKey}>
                                        <HeroClusterIconWrap
                                            $color={COMPETITOR_ICON_STYLE[appKey]?.color}
                                            $background={COMPETITOR_ICON_STYLE[appKey]?.background || 'rgba(0, 0, 0, 0.06)'}
                                        >
                                            {renderAppIcon(appKey, 16)}
                                        </HeroClusterIconWrap>
                                        <span>{COMPETITOR_PRICING[appKey]?.name || appKey}</span>
                                    </HeroClusterItem>
                                ))}
                            </HeroAppsCluster>
                        ) : page.competitorIcon && !page.hideCompetitorHeroIcon ? (
                            <VsLogosWrap>
                                <VsLogoCircle $background="rgba(43, 136, 217, 0.1)">
                                    <img src={luckyIcon} alt="Luckynote" />
                                </VsLogoCircle>
                                <VsLabel>VS</VsLabel>
                                <VsLogoCircle
                                    $color={COMPETITOR_ICON_STYLE[page.competitorIcon]?.color}
                                    $background={COMPETITOR_ICON_STYLE[page.competitorIcon]?.background}
                                >
                                    {COMPETITOR_ICONS[page.competitorIcon]?.component
                                        ? React.createElement(COMPETITOR_ICONS[page.competitorIcon].component, { size: 20 })
                                        : COMPETITOR_ICONS[page.competitorIcon]?.image
                                            ? <img src={COMPETITOR_ICONS[page.competitorIcon].image} alt="" />
                                            : <VsMonogram>{COMPETITOR_ICONS[page.competitorIcon]?.letter}</VsMonogram>}
                                </VsLogoCircle>
                            </VsLogosWrap>
                        ) : null}
                        {HeroIconComponent && !page.hideHeroIcon && !isPersonaPage ? (
                            <HeroIconBadge
                                $color={heroChipMeta.color}
                                $background={heroChipMeta.background}
                            >
                                <HeroIconComponent size={22} />
                            </HeroIconBadge>
                        ) : null}
                        {page.hero.eyebrow && (!isEditorial || isEditorialPersona) ? (
                            <Eyebrow
                                $editorial={isEditorial}
                                $personaTag={isPersonaPage}
                                $color={heroChipMeta.color}
                                $background={heroChipMeta.background}
                            >
                                {page.hero.eyebrow}
                            </Eyebrow>
                        ) : null}
                        <HeroTitle
                            $editorial={isEditorial}
                            $editorialPersona={isEditorialPersona}
                            $persona={isPersonaPage}
                        >
                            {page.hero.title}
                        </HeroTitle>
                        <HeroDescription $align={heroAlign} $editorial={isEditorial}>{page.hero.description}</HeroDescription>
                        {!page.hideHeroActions ? (
                            <ActionRow $align={heroAlign}>
                                <ActionButton action={primaryAction} />
                                {secondaryAction ? <ActionButton action={secondaryAction} secondary /> : null}
                            </ActionRow>
                        ) : null}
                        {page.kind !== 'download' && !page.hideAuthorByline ? (
                            <AuthorByline
                                align={heroAlign}
                                datePublished={page.datePublished}
                                dateModified={page.lastmod}
                                categoryLabel={editorialCategoryLabel}
                            />
                        ) : null}
                    </HeroContent>
                    {!shouldHideUseCaseHeroImage && page.hero.image ? (
                        <HeroImageWrap $imageSize={page.hero.imageSize}>
                            <img src={IMAGE_MAP[page.hero.image] || page.hero.image} alt={page.hero.imageAlt || ''} />
                        </HeroImageWrap>
                    ) : !shouldHideUseCaseHeroImage && page.hero.imagePlaceholder ? (
                        <HeroImageWrap>
                            <ImagePlaceholder>{page.hero.imagePlaceholder}</ImagePlaceholder>
                        </HeroImageWrap>
                    ) : null}
                </HeroSection>

                {page.sections.map((section, index) => {
                    const tinted = !isEditorial && index % 2 === 1;
                    if (section.type === 'text') {
                        return (
                            <ContentSection key={section.title} id={section.id} $tinted={tinted} $editorial={isEditorial}>
                                <SectionHeading $editorial={isEditorial}>{section.title}</SectionHeading>
                                {section.description ? <SectionDescription $editorial={isEditorial}>{section.description}</SectionDescription> : null}
                                {(section.paragraphs || []).map((paragraph) => (
                                    <Paragraph key={paragraph} $editorial={isEditorial}>{paragraph}</Paragraph>
                                ))}
                            </ContentSection>
                        );
                    }

                    if (section.type === 'editorial_list') {
                        return (
                            <ContentSection key={section.title} id={section.id} $tinted={tinted} $editorial={isEditorial}>
                                <SectionHeading $editorial={isEditorial}>{section.title}</SectionHeading>
                                {section.description ? <SectionDescription $editorial={isEditorial}>{section.description}</SectionDescription> : null}
                                <EditorialList>
                                    {section.items.map((item) => (
                                        <EditorialListItem key={`${section.title}-${item.title}`}>
                                            <EditorialListTitle>{item.title}</EditorialListTitle>
                                            <EditorialListBody>{item.body}</EditorialListBody>
                                        </EditorialListItem>
                                    ))}
                                </EditorialList>
                            </ContentSection>
                        );
                    }

                    if (section.type === 'editorial_steps') {
                        return (
                            <ContentSection key={section.title} id={section.id} $tinted={tinted} $editorial={isEditorial}>
                                <SectionHeading $editorial={isEditorial}>{section.title}</SectionHeading>
                                {section.description ? <SectionDescription $editorial={isEditorial}>{section.description}</SectionDescription> : null}
                                <EditorialSteps>
                                    {section.items.map((item, itemIndex) => (
                                        <EditorialStep key={`${section.title}-${item.title}`}>
                                            <EditorialStepNumber>{itemIndex + 1}</EditorialStepNumber>
                                            <EditorialStepCopy>
                                                <EditorialStepTitle>{item.title}</EditorialStepTitle>
                                                <EditorialStepBody>{item.body}</EditorialStepBody>
                                                {item.to ? (
                                                    <EditorialStepLink
                                                        to={item.to}
                                                        target={item.openInNewTab ? '_blank' : undefined}
                                                        rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
                                                    >
                                                        {item.linkLabel || 'Learn more →'}
                                                    </EditorialStepLink>
                                                ) : null}
                                            </EditorialStepCopy>
                                        </EditorialStep>
                                    ))}
                                </EditorialSteps>
                            </ContentSection>
                        );
                    }

                    if (section.type === 'ranked_list') {
                        return (
                            <ContentSection key={section.title} id={section.id} $tinted={tinted} $editorial={isEditorial}>
                                <SectionHeading $editorial={isEditorial}>{section.title}</SectionHeading>
                                {section.description ? <SectionDescription $editorial={isEditorial}>{section.description}</SectionDescription> : null}
                                <RankedList>
                                    {section.items.map((item, index) => (
                                        <RankedItem key={`${section.title}-${item.title}`}>
                                            <RankBadge $primary={index === 0}>{index + 1}</RankBadge>
                                            <RankCopy>
                                                <strong>{item.title}</strong>
                                                <span>{item.body}</span>
                                            </RankCopy>
                                        </RankedItem>
                                    ))}
                                </RankedList>
                            </ContentSection>
                        );
                    }

                    if (section.type === 'stats') {
                        return (
                            <ContentSection key={section.title} id={section.id} $tinted={tinted} $editorial={isEditorial}>
                                <SectionHeading $editorial={isEditorial}>{section.title}</SectionHeading>
                                <StatsGrid>
                                    {section.items.map((item) => (
                                        <StatCard key={`${section.title}-${item.title}`}>
                                            <StatValue>{item.title}</StatValue>
                                            <StatLabel>{item.body}</StatLabel>
                                        </StatCard>
                                    ))}
                                </StatsGrid>
                            </ContentSection>
                        );
                    }

                    if (section.type === 'app_comparison_table') {
                        const renderAppTableCell = (value) => {
                            if (value === 'Yes') return <AppTablePositive>✓ Yes</AppTablePositive>;
                            if (value === 'No') return <AppTableMuted>— No</AppTableMuted>;
                            if (value === 'Paste-in') return <AppTablePositive>✓ Paste-in</AppTablePositive>;
                            return value;
                        };

                        return (
                            <ContentSection key={section.title} id={section.id} $tinted={tinted} $editorial={isEditorial}>
                                <SectionHeading $editorial={isEditorial}>{section.title}</SectionHeading>
                                {section.description ? <SectionDescription $editorial={isEditorial}>{section.description}</SectionDescription> : null}
                                <AppTableWrap>
                                    <TableScroll>
                                        <AppComparisonTable>
                                            <thead>
                                                <tr>
                                                    {section.columns.map((column) => <th key={column}>{column}</th>)}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {section.rows.map((row) => (
                                                    <tr key={row.app} className={row.featured ? 'featured' : undefined}>
                                                        <td>
                                                            <AppName>
                                                                <AppIconBadge $featured={row.featured}>
                                                                    {renderAppIcon(row.app, 18)}
                                                                </AppIconBadge>
                                                                <span>{row.app}</span>
                                                            </AppName>
                                                        </td>
                                                        {row.cells ? (
                                                            row.cells.map((cell, cellIdx) => (
                                                                <td key={cellIdx}>{renderAppTableCell(cell)}</td>
                                                            ))
                                                        ) : (
                                                            <>
                                                                <td>{row.bestFor}</td>
                                                                <td>{renderAppTableCell(row.freePlan)}</td>
                                                                <td>{renderAppTableCell(row.fullTextSearch)}</td>
                                                                <td>{renderAppTableCell(row.savesToTasks)}</td>
                                                                <td>{renderAppTableCell(row.pocketImport)}</td>
                                                                <td>{row.from}</td>
                                                            </>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </AppComparisonTable>
                                    </TableScroll>
                                </AppTableWrap>
                            </ContentSection>
                        );
                    }

                    if (section.type === 'alternative_cards') {
                        return (
                            <ContentSection key={section.title} id={section.id} $tinted={tinted} $editorial={isEditorial}>
                                <SectionHeading $editorial={isEditorial}>{section.title}</SectionHeading>
                                {section.description ? <SectionDescription $editorial={isEditorial}>{section.description}</SectionDescription> : null}
                                <AlternativeCardList>
                                    {section.items.map((item, index) => (
                                        <AlternativeCard key={`${section.title}-${item.title}`} $featured={item.featured}>
                                            <AlternativeRank $primary={index === 0}>{index + 1}</AlternativeRank>
                                            <AlternativeMain>
                                                <AlternativeTitleRow>
                                                    <AlternativeAppIconBadge $featured={item.featured}>
                                                        {renderAppIcon(item.title, 20)}
                                                    </AlternativeAppIconBadge>
                                                    <AlternativeTitle>{item.title}</AlternativeTitle>
                                                    {(item.badges || []).map((badge, badgeIndex) => (
                                                        <AlternativeBadge
                                                            key={`${item.title}-${badge}`}
                                                            $featured={item.featured && badgeIndex === 0}
                                                        >
                                                            {badge}
                                                        </AlternativeBadge>
                                                    ))}
                                                </AlternativeTitleRow>
                                                <AlternativeBody>{item.body}</AlternativeBody>
                                                <AlternativeMeta>
                                                    <strong>Platforms:</strong> {item.platforms}
                                                    {item.aiSearch ? (
                                                        <>
                                                            <AlternativeMetaSeparator aria-hidden="true">·</AlternativeMetaSeparator>
                                                            <strong>AI search:</strong> {item.aiSearch}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <AlternativeMetaSeparator aria-hidden="true">·</AlternativeMetaSeparator>
                                                            <strong>Strength:</strong> {item.strength}
                                                        </>
                                                    )}
                                                    {item.rating ? (
                                                        <>
                                                            <AlternativeMetaSeparator aria-hidden="true">·</AlternativeMetaSeparator>
                                                            <strong>Rating:</strong> {item.rating}
                                                        </>
                                                    ) : null}
                                                </AlternativeMeta>
                                            </AlternativeMain>
                                            <AlternativeAside>
                                                <AlternativePrice>{item.price}</AlternativePrice>
                                                <AlternativePriceNote>{item.priceNote}</AlternativePriceNote>
                                                {item.ctaTo ? (
                                                    <AlternativeCta to={item.ctaTo} $featured={item.featured}>{item.ctaLabel || 'Learn more'}</AlternativeCta>
                                                ) : item.ctaHref ? (
                                                    <AlternativeCtaExternal href={item.ctaHref} target="_blank" rel="noopener noreferrer">
                                                        {item.ctaLabel || 'Learn more'}
                                                    </AlternativeCtaExternal>
                                                ) : null}
                                            </AlternativeAside>
                                        </AlternativeCard>
                                    ))}
                                </AlternativeCardList>
                            </ContentSection>
                        );
                    }

                    if (section.type === 'grid' || section.type === 'steps' || section.type === 'feature_grid') {
                        return (
                            <ContentSection key={section.title} id={section.id} $tinted={tinted} $editorial={isEditorial}>
                                <SectionHeading $editorial={isEditorial}>{section.title}</SectionHeading>
                                {section.description ? <SectionDescription $editorial={isEditorial}>{section.description}</SectionDescription> : null}
                                <CardGrid $columns={section.type === 'steps' && !isEditorial ? 2 : 3} $editorial={isEditorial}>
                                    {section.items.map((item, index) => {
                                        const ItemIconComponent = item.icon ? FiIcons[item.icon] : null;
                                        return (
                                        <InfoCard key={`${section.title}-${item.title}`} $editorial={isEditorial}>
                                            {section.type === 'steps' ? <StepNumber>{index + 1}</StepNumber> : null}
                                            {(section.type === 'grid' || section.type === 'feature_grid') && ItemIconComponent ? (
                                                <ItemIcon>
                                                    <ItemIconComponent size={18} />
                                                </ItemIcon>
                                            ) : null}
                                            <CardTitle>{item.title}</CardTitle>
                                            <CardBody>{item.body}</CardBody>
                                            {item.image ? (
                                                <ItemImageWrap>
                                                    <img src={item.image} alt={item.imageAlt || ''} />
                                                </ItemImageWrap>
                                            ) : item.imagePlaceholder ? (
                                                <ItemImageWrap>
                                                    <ImagePlaceholder $small>{item.imagePlaceholder}</ImagePlaceholder>
                                                </ItemImageWrap>
                                            ) : null}
                                        </InfoCard>
                                        );
                                    })}
                                </CardGrid>
                            </ContentSection>
                        );
                    }

                    if (section.type === 'feature_strip') {
                        return (
                            <ContentSection key={section.title} id={section.id} $tinted={tinted} $editorial={isEditorial}>
                                <SectionHeading $editorial={isEditorial}>{section.title}</SectionHeading>
                                {section.description ? <SectionDescription $editorial={isEditorial}>{section.description}</SectionDescription> : null}
                                <FeatureStrip aria-label={section.title}>
                                    {section.items.map((item) => (
                                        <FeatureChip key={`${section.title}-${item.title}`}>{item.title}</FeatureChip>
                                    ))}
                                </FeatureStrip>
                            </ContentSection>
                        );
                    }

                    if (section.type === 'comparison') {
                        const renderCell = (value) => {
                            if (value === 'Yes') return <ComparisonYes>✓ Yes</ComparisonYes>;
                            if (value === 'No') return <ComparisonNo>✕ No</ComparisonNo>;
                            if (value === 'Limited') return <ComparisonLimited>~ Limited</ComparisonLimited>;
                            return value;
                        };
                        const renderHead = () => (
                            <thead>
                                <tr>
                                    <th>Feature</th>
                                    <th>
                                        <TableHeaderLabel>
                                            <TableHeaderIcon $background="rgba(43, 136, 217, 0.12)">
                                                <img src={luckyIcon} alt="" width={16} height={16} />
                                            </TableHeaderIcon>
                                            {section.columns[0]}
                                        </TableHeaderLabel>
                                    </th>
                                    <th>
                                        <TableHeaderLabel>
                                            {page.competitorIcon ? (
                                                <TableHeaderIcon
                                                    $color={COMPETITOR_ICON_STYLE[page.competitorIcon]?.color}
                                                    $background={COMPETITOR_ICON_STYLE[page.competitorIcon]?.background}
                                                >
                                                    {COMPETITOR_ICONS[page.competitorIcon]?.component
                                                        ? React.createElement(COMPETITOR_ICONS[page.competitorIcon].component, { size: 12 })
                                                        : COMPETITOR_ICONS[page.competitorIcon]?.image
                                                            ? <img src={COMPETITOR_ICONS[page.competitorIcon].image} alt="" width={16} height={16} />
                                                            : <TableHeaderMonogram>{COMPETITOR_ICONS[page.competitorIcon]?.letter}</TableHeaderMonogram>}
                                                </TableHeaderIcon>
                                            ) : null}
                                            {section.columns[1]}
                                        </TableHeaderLabel>
                                    </th>
                                </tr>
                            </thead>
                        );

                        if (section.categories) {
                            return (
                                <ContentSection key={section.title} id={section.id} $tinted={tinted}>
                                    <SectionHeading>{section.title}</SectionHeading>
                                    <CategoryTableGrid>
                                        {section.categories.map((category) => (
                                            <CategoryTableBlock key={category.name}>
                                                <CategoryTableHeading>{category.name}</CategoryTableHeading>
                                                <TableWrap>
                                                    <TableScroll>
                                                        <ComparisonTable>
                                                            {renderHead()}
                                                            <tbody>
                                                                {category.rows.map((row) => (
                                                                    <tr key={row.feature}>
                                                                        <td>{row.feature}</td>
                                                                        <td>{renderCell(row.left)}</td>
                                                                        <td>{renderCell(row.right)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </ComparisonTable>
                                                    </TableScroll>
                                                </TableWrap>
                                            </CategoryTableBlock>
                                        ))}
                                    </CategoryTableGrid>
                                </ContentSection>
                            );
                        }

                        return (
                            <ContentSection key={section.title} id={section.id} $tinted={tinted}>
                                <SectionHeading>{section.title}</SectionHeading>
                                <TableWrap>
                                    <TableScroll>
                                        <ComparisonTable>
                                            {renderHead()}
                                            <tbody>
                                                {section.rows.map((row) => (
                                                    <tr key={row.feature}>
                                                        <td>{row.feature}</td>
                                                        <td>{renderCell(row.left)}</td>
                                                        <td>{renderCell(row.right)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </ComparisonTable>
                                    </TableScroll>
                                </TableWrap>
                                {comparisonPricing && section.rows?.some((row) => row.feature === 'Price') ? (
                                    <ComparisonMeta>
                                        Pricing checked <time dateTime={comparisonPricing.asOf}>{formatDate(comparisonPricing.asOf)}</time>
                                        <ComparisonMetaSeparator aria-hidden="true">·</ComparisonMetaSeparator>
                                        {comparisonPricing.sourceUrl ? (
                                            <>
                                                <ComparisonMetaLink
                                                    href={comparisonPricing.sourceUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {comparisonPricing.name}
                                                </ComparisonMetaLink>{' '}
                                                pricing from their official pricing page
                                            </>
                                        ) : (
                                            `${comparisonPricing.name} pricing from their official pricing page`
                                        )}
                                    </ComparisonMeta>
                                ) : null}
                            </ContentSection>
                        );
                    }

                    if (section.type === 'versus') {
                        const leftIdentity = getVersusColumnIdentity(section, 'left', page, competitorDisplayName);
                        const rightIdentity = getVersusColumnIdentity(section, 'right', page, competitorDisplayName);
                        const leftIconVariants = getStrengthIconVariants(section.left.items);
                        const rightIconVariants = getStrengthIconVariants(section.right.items);

                        return (
                            <ContentSection key={section.title} id={section.id} $tinted={tinted}>
                                <VersusIntro>
                                    <VersusEyebrow>What each excels at</VersusEyebrow>
                                    <SectionHeading>{section.title}</SectionHeading>
                                    <SectionDescription>
                                        {section.description || 'Every tool has areas where it shines. Here is an honest look at what each product does well.'}
                                    </SectionDescription>
                                </VersusIntro>
                                <VersusGrid>
                                    <VersusColumn>
                                        <VersusColumnHeader>
                                            <VersusColumnIdentity>
                                                {renderVersusLogo(leftIdentity.app)}
                                                <VersusColumnTitle>{leftIdentity.name}</VersusColumnTitle>
                                            </VersusColumnIdentity>
                                        </VersusColumnHeader>
                                        <VersusColumnDescription>{section.left.heading}</VersusColumnDescription>
                                        <VersusColumnDivider />
                                        <VersusCard>
                                            <VersusList>
                                                {section.left.items.map((item, index) => (
                                                    <VersusListItem key={item}>
                                                        <StrengthIconWrap>
                                                            <StrengthIcon variant={leftIconVariants[index]} />
                                                        </StrengthIconWrap>
                                                        <span>{item}</span>
                                                    </VersusListItem>
                                                ))}
                                            </VersusList>
                                        </VersusCard>
                                    </VersusColumn>
                                    <VersusColumn>
                                        <VersusColumnHeader>
                                            <VersusColumnIdentity>
                                                {renderVersusLogo(rightIdentity.app)}
                                                <VersusColumnTitle>{rightIdentity.name}</VersusColumnTitle>
                                            </VersusColumnIdentity>
                                        </VersusColumnHeader>
                                        <VersusColumnDescription>{section.right.heading}</VersusColumnDescription>
                                        <VersusColumnDivider />
                                        <VersusCard>
                                            <VersusList>
                                                {section.right.items.map((item, index) => (
                                                    <VersusListItem key={item}>
                                                        <StrengthIconWrap>
                                                            <StrengthIcon variant={rightIconVariants[index]} />
                                                        </StrengthIconWrap>
                                                        <span>{item}</span>
                                                    </VersusListItem>
                                                ))}
                                            </VersusList>
                                        </VersusCard>
                                    </VersusColumn>
                                </VersusGrid>
                            </ContentSection>
                        );
                    }

                    if (section.type === 'downloads') {
                        return (
                            <ContentSection key={section.title} id={section.id} $tinted={tinted}>
                                <SectionHeading>{section.title}</SectionHeading>
                                <DownloadGrid>
                                    {section.items.map((item) => (
                                        <DownloadCard key={item.name}>
                                            {item.icon ? (
                                                <DownloadIconWrap
                                                    $color={DOWNLOAD_ICON_MAP[item.icon]?.color}
                                                    $background={DOWNLOAD_ICON_MAP[item.icon]?.background}
                                                >
                                                    {React.createElement(
                                                        DOWNLOAD_ICON_MAP[item.icon]?.component || 'span',
                                                        { size: 28 }
                                                    )}
                                                </DownloadIconWrap>
                                            ) : null}
                                            <CardTitle>{item.name}</CardTitle>
                                            <CardBody>{item.description}</CardBody>
                                            {item.platformKey ? (
                                                <ActionButtonElement
                                                    type="button"
                                                    onClick={() => downloadLatestDesktopArtifact(item.platformKey, item.urlKey || 'download_url')}
                                                >
                                                    {item.label}
                                                </ActionButtonElement>
                                            ) : item.href ? (
                                                <ActionAnchor href={item.href} target="_blank" rel="noopener noreferrer">
                                                    {item.label}
                                                </ActionAnchor>
                                            ) : (
                                                <DisabledAction aria-disabled="true">
                                                    {item.label || 'Coming soon'}
                                                </DisabledAction>
                                            )}
                                        </DownloadCard>
                                    ))}
                                </DownloadGrid>
                            </ContentSection>
                        );
                    }

                    if (section.type === 'downloadHub') {
                        const primaryDownloads = section.desktopItems?.slice(0, 2) || [];
                        const groupedDownloads = [
                            {
                                title: 'Desktop & Web',
                                icon: 'web',
                                items: section.desktopItems || []
                            },
                            {
                                title: 'Mobile & Tablet',
                                icon: 'web',
                                items: section.mobileItems || []
                            }
                        ];

                        return (
                            <DownloadHubSection key={section.title} id={section.id}>
                                <PrimaryDownloadStack>
                                    {primaryDownloads.map((item, index) => (
                                        <DownloadAction
                                            key={item.name}
                                            item={item}
                                            secondary={index > 0}
                                        />
                                    ))}
                                </PrimaryDownloadStack>
                                <DownloadPanelGrid>
                                    {groupedDownloads.map((group) => (
                                        <DownloadPanel key={group.title}>
                                            <DownloadPanelTitle>
                                                <DownloadPanelIcon>
                                                    {React.createElement(DOWNLOAD_ICON_MAP[group.icon]?.component || FaGlobe, { size: 16 })}
                                                </DownloadPanelIcon>
                                                {group.title}
                                            </DownloadPanelTitle>
                                            <DownloadList>
                                                {group.items.map((item) => (
                                                    <DownloadListItem key={item.name}>
                                                        <PlatformSummary>
                                                            {item.icon ? (
                                                                React.createElement(DOWNLOAD_ICON_MAP[item.icon]?.component || 'span', { size: 16 })
                                                            ) : null}
                                                            <span>{item.name}</span>
                                                        </PlatformSummary>
                                                        <DownloadAction item={item} secondary compact />
                                                    </DownloadListItem>
                                                ))}
                                            </DownloadList>
                                        </DownloadPanel>
                                    ))}
                                </DownloadPanelGrid>
                            </DownloadHubSection>
                        );
                    }

                    if (section.type === 'testimonial') {
                        const items = getTestimonialsByIds(section.ids);
                        if (!items.length) {
                            return null;
                        }
                        return (
                            <TestimonialSection key={section.title || section.ids.join(',')} id={section.id}>
                                {section.title ? <SectionHeading>{section.title}</SectionHeading> : null}
                                <TestimonialGrid>
                                    {items.map((testimonial) => (
                                        <TestimonialItem key={testimonial.id}>
                                            <TestimonialQuoteMark aria-hidden="true">&ldquo;</TestimonialQuoteMark>
                                            <TestimonialQuote>{testimonial.text}</TestimonialQuote>
                                            <TestimonialAttribution>
                                                {testimonial.avatar ? (
                                                    <TestimonialAvatar src={testimonial.avatar} alt={testimonial.author} width={40} height={40} />
                                                ) : (
                                                    <TestimonialAvatarFallback>
                                                        {testimonial.author
                                                            .split(' ')
                                                            .map((part) => part[0])
                                                            .join('')
                                                            .toUpperCase()}
                                                    </TestimonialAvatarFallback>
                                                )}
                                                <TestimonialAuthorMeta>
                                                    <TestimonialAuthorName>
                                                        {testimonial.link ? (
                                                            <TestimonialAuthorLink
                                                                href={testimonial.link}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {testimonial.author}
                                                            </TestimonialAuthorLink>
                                                        ) : (
                                                            testimonial.author
                                                        )}
                                                    </TestimonialAuthorName>
                                                    <TestimonialAuthorRole>{testimonial.role}</TestimonialAuthorRole>
                                                </TestimonialAuthorMeta>
                                            </TestimonialAttribution>
                                        </TestimonialItem>
                                    ))}
                                </TestimonialGrid>
                            </TestimonialSection>
                        );
                    }

                    return null;
                })}

                {page.faqItems?.length ? (
                    <ContentSection $editorial={isEditorial}>
                        <SectionHeading $editorial={isEditorial}>Frequently asked questions</SectionHeading>
                        <FaqList $editorial={isEditorial}>
                            {page.faqItems.map((item, index) => (
                                <FaqItem
                                    key={item.question}
                                    as={isEditorial ? 'details' : 'article'}
                                    open={isEditorial && index === 0}
                                    $editorial={isEditorial}
                                >
                                    <FaqQuestion as={isEditorial ? 'summary' : 'h3'} $editorial={isEditorial}>
                                        <span>{item.question}</span>
                                        {isEditorial ? <FaqToggle aria-hidden="true">+</FaqToggle> : null}
                                    </FaqQuestion>
                                    <FaqAnswer>
                                        {item.answerLink ? (
                                            <>
                                                {item.answerBeforeLink}
                                                <FaqAnswerLink
                                                    to={item.answerLink.to}
                                                    target={item.answerLink.openInNewTab ? '_blank' : undefined}
                                                    rel={item.answerLink.openInNewTab ? 'noopener noreferrer' : undefined}
                                                >
                                                    {item.answerLink.label}
                                                </FaqAnswerLink>
                                                {item.answerAfterLink}
                                            </>
                                        ) : item.answer}
                                    </FaqAnswer>
                                </FaqItem>
                            ))}
                        </FaqList>
                    </ContentSection>
                ) : null}

                {showCrossLinks && similarLinks.length ? (
                    <ContentSection $editorial={isEditorial}>
                        <SectionHeading $editorial={isEditorial}>{similarHeading}</SectionHeading>
                        <CompactLinkGrid>
                            {similarLinks.map((link) => (
                                <CompactLinkCard key={`${page.path}-${link.to}`} to={link.to}>
                                    <CompactLinkChip
                                        $color={link.iconColor}
                                        $background={link.iconBackground}
                                    >
                                        {link.icon && FiIcons[link.icon]
                                            ? React.createElement(FiIcons[link.icon], { size: 14 })
                                            : <FiIcons.FiArrowUpRight size={14} />}
                                    </CompactLinkChip>
                                    <CompactLinkTitle>{link.label}</CompactLinkTitle>
                                </CompactLinkCard>
                            ))}
                        </CompactLinkGrid>
                    </ContentSection>
                ) : null}

                {showCrossLinks && exploreLinks.length ? (
                    <ContentSection $editorial={isEditorial}>
                        <SectionHeading $editorial={isEditorial}>{exploreHeading}</SectionHeading>
                        <CompactLinkGrid>
                            {exploreLinks.map((link) => (
                                <CompactLinkCard key={`${page.path}-${link.to}`} to={link.to}>
                                    <CompactLinkChip
                                        $color={link.iconColor}
                                        $background={link.iconBackground}
                                    >
                                        {link.icon && FiIcons[link.icon]
                                            ? React.createElement(FiIcons[link.icon], { size: 14 })
                                            : <FiIcons.FiArrowUpRight size={14} />}
                                    </CompactLinkChip>
                                    <CompactLinkTitle>{link.label}</CompactLinkTitle>
                                </CompactLinkCard>
                            ))}
                        </CompactLinkGrid>
                    </ContentSection>
                ) : null}

                {!page.hideFinalCta ? (
                    <FinalCta $editorial={isEditorial}>
                        <SectionHeading $editorial={isEditorial}>
                            {page.finalCta?.title || (isEditorial ? 'Keep the save-it-fast habit. Lose the pile you never read.' : 'Capture and find what matters')}
                        </SectionHeading>
                        <SectionDescription $editorial={isEditorial}>
                            {page.finalCta?.description || (isEditorial
                                ? "Luckynote gives your saved links a home where they're easy to find and easy to act on, not another queue that quietly becomes a backlog."
                                : 'Keep the fast capture habit, but give yourself a better place to return to later.')}
                        </SectionDescription>
                        <ActionRow $align={isEditorial ? 'center' : undefined}>
                            <ActionButton action={primaryAction} />
                            {!isEditorial && secondaryAction ? <ActionButton action={secondaryAction} secondary /> : null}
                        </ActionRow>
                    </FinalCta>
                ) : null}
            </PageShell>
        </LayoutDefault>
    );
};

const PageShell = styled.div`
    max-width: ${({ $editorial }) => ($editorial ? '1180px' : '1120px')};
    margin: 0 auto;
    padding: ${({ $editorial }) => ($editorial ? '108px 24px 96px' : '56px 20px 96px')};
    ${({ $editorial }) => $editorial && `
        background: #fbfaf7;
        box-shadow:
            50vw 0 0 #fbfaf7,
            -50vw 0 0 #fbfaf7;
        color: #14110f;
    `}
    ${({ $compact }) => $compact && `
        padding-top: 44px;
        padding-bottom: 72px;

        > section {
            padding-top: 64px;
            padding-bottom: 64px;
        }

        > section:first-child {
            padding-top: 72px;
            padding-bottom: 56px;
        }

        h1 {
            font-size: clamp(1.9rem, 3vw, 2.45rem);
            line-height: 1.14;
        }

        h2 {
            font-size: clamp(1.5rem, 2.4vw, 2rem);
            line-height: 1.22;
            margin-bottom: 28px;
        }

        h3 {
            font-size: 1rem;
        }

        p {
            font-size: 0.96rem;
            line-height: 1.65;
        }
    `}

    @media (max-width: 640px) {
        padding: ${({ $editorial }) => ($editorial ? '44px 18px 72px' : '56px 20px 96px')};

        ${({ $compact }) => $compact && `
            padding-top: 36px;
            padding-bottom: 56px;

            > section,
            > section:first-child {
                padding-top: 48px;
                padding-bottom: 48px;
            }
        `}
    }
`;

const HeroSection = styled.section`
    padding: ${({ $editorial }) => ($editorial ? '0 0 56px' : '32px 0 72px')};
    border-bottom: ${({ $hideBorder, $editorial }) => ($hideBorder || $editorial ? 'none' : '1px solid rgba(0, 0, 0, 0.06)')};
    display: ${({ $hasImage }) => ($hasImage ? 'grid' : 'block')};
    grid-template-columns: ${({ $hasImage }) => ($hasImage ? '1fr 1fr' : 'unset')};
    align-items: center;
    gap: 48px;
    text-align: ${({ $align }) => ($align === 'center' ? 'center' : 'left')};

    @media (max-width: 768px) {
        display: block;
    }
`;

const HeroContent = styled.div`
    max-width: ${({ $editorial, $hasImage }) => ($editorial ? '840px' : ($hasImage ? 'unset' : '760px'))};
    margin: ${({ $align, $hasImage }) => ($align === 'center' && !$hasImage ? '0 auto' : '0')};
`;

const EditorialBreadcrumb = styled.nav`
    align-items: center;
    color: rgba(20, 17, 15, 0.58);
    display: inline-flex;
    flex-wrap: wrap;
    font-size: 14px;
    font-weight: 600;
    gap: 10px;
    line-height: 1.4;
    margin: 0 0 34px;
`;

const HeroImageWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    img {
        width: ${({ $imageSize }) => $imageSize || '100%'};
        height: auto;
        border-radius: 8px;
    }

    @media (max-width: 768px) {
        margin-top: 32px;

        img {
            width: 100%;
            max-width: 100%;
        }
    }
`;

const VsLogosWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 20px;
`;

const VsLogoCircle = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: ${({ $background }) => $background || '#fff'};
    border: 1px solid rgba(0, 0, 0, 0.08);
    color: ${({ $color }) => $color || '#1a1a1a'};

    img {
        width: 20px;
        height: 20px;
        border-radius: 4px;
        object-fit: contain;
    }
`;

const VsLabel = styled.span`
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: #9ca3af;
    flex-shrink: 0;
`;

// Fallback for competitors with no Simple Icons brand glyph and no local
// image asset (e.g. Raindrop.io, Otter.ai) - a plain monogram letter is
// safer than guessing at an unofficial logo image.
const VsMonogram = styled.span`
    font-size: 0.95rem;
    font-weight: 700;
`;

const TableHeaderMonogram = styled.span`
    font-size: 0.7rem;
    font-weight: 700;
`;

const ImagePlaceholder = styled.div`
    width: 100%;
    height: ${({ $small }) => ($small ? '160px' : '320px')};
    background: #f0f2f5;
    border: 2px dashed #c8cdd4;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
    font-size: ${({ $small }) => ($small ? '0.8rem' : '0.875rem')};
    font-weight: 500;
    text-align: center;
    padding: 16px;
    margin-top: ${({ $small }) => ($small ? '16px' : '0')};
`;

const ItemImageWrap = styled.div`
    margin-top: 16px;

    img {
        width: 100%;
        height: auto;
        border-radius: 6px;
    }
`;

const Eyebrow = styled.p`
    margin: 0 0 ${({ $editorial }) => ($editorial ? '14px' : '12px')};
    font-size: ${({ $editorial }) => ($editorial ? '0.88rem' : '0.9rem')};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ $color }) => $color || '#2b88d9'};
    font-weight: 600;
    ${({ $personaTag, $background }) => $personaTag && `
        background: ${$background || 'rgba(43, 136, 217, 0.1)'};
        border-radius: 999px;
        display: inline-flex;
        padding: 7px 14px;
        text-transform: none;
        letter-spacing: 0;
    `}
`;

const HeroIconBadge = styled.div`
    width: 44px;
    height: 44px;
    margin: 0 0 16px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ $background }) => $background};
    color: ${({ $color }) => $color};
`;

const HeroTitle = styled.h1`
    margin: 0 0 ${({ $editorial }) => ($editorial ? '18px' : '20px')};
    font-family: ${({ $editorial, $persona }) => ($editorial || $persona ? "Georgia, 'Iowan Old Style', 'Palatino Linotype', 'Book Antiqua', Palatino, serif" : 'inherit')};
    font-size: ${({ $editorial, $editorialPersona, $persona }) => ($persona
        ? 'clamp(2.25rem, 4vw, 3.25rem)'
        : ($editorial
            ? ($editorialPersona ? 'clamp(2.25rem, 4vw, 3.25rem)' : 'clamp(48px, 7vw, 76px)')
            : 'clamp(1.8rem, 3.5vw, 3rem)'))};
    font-weight: ${({ $editorial, $persona }) => ($editorial || $persona ? '700' : 'inherit')};
    line-height: ${({ $editorial, $persona }) => ($editorial || $persona ? '1.02' : '1.1')};
    letter-spacing: 0;
    color: #1a1a1a;
    max-width: ${({ $editorial }) => ($editorial ? '820px' : 'none')};
    text-wrap: balance;

    @media (max-width: 640px) {
        font-size: ${({ $editorial, $editorialPersona, $persona }) => ($persona
            ? '2.25rem'
            : ($editorial
                ? ($editorialPersona ? '2.25rem' : '42px')
                : 'clamp(1.8rem, 3.5vw, 3rem)'))};
    }
`;

const HeroDescription = styled.p`
    margin: 0;
    font-size: ${({ $editorial }) => ($editorial ? '18px' : '1.15rem')};
    line-height: ${({ $editorial }) => ($editorial ? '1.62' : '1.75')};
    color: ${({ $editorial }) => ($editorial ? 'rgba(43, 47, 54, 0.74)' : '#555')};
    max-width: ${({ $editorial }) => ($editorial ? '880px' : '42rem')};
    margin-inline: ${({ $align }) => ($align === 'center' ? 'auto' : '0')};
    text-wrap: pretty;
`;

const ActionRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    margin-top: 32px;
    justify-content: ${({ $align }) => ($align === 'center' ? 'center' : 'flex-start')};
`;

const actionStyles = `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 48px;
    padding: 0 32px;
    border-radius: 999px;
    font-size: 0.875rem;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;

    &:hover {
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }
`;

const ActionLink = styled(Link)`
    ${actionStyles}
    background: ${({ $secondary }) => ($secondary ? '#fff' : '#2b88d9')};
    color: ${({ $secondary }) => ($secondary ? '#2b88d9' : '#fff')} !important;
    border: 1px solid ${({ $secondary }) => ($secondary ? '#2b88d9' : 'transparent')};
    box-shadow: ${({ $secondary }) => ($secondary ? 'none' : '0 2px 10px rgba(43, 136, 217, 0.3)')};

    &:hover {
        background: ${({ $secondary }) => ($secondary ? 'rgba(43, 136, 217, 0.06)' : '#3a96e8')};
        color: ${({ $secondary }) => ($secondary ? '#2b88d9' : '#fff')} !important;
        box-shadow: ${({ $secondary }) => ($secondary ? 'none' : '0 4px 16px rgba(43, 136, 217, 0.4)')};
    }
`;

const ActionAnchor = styled.a`
    ${actionStyles}
    background: ${({ $secondary }) => ($secondary ? '#fff' : '#2b88d9')};
    color: ${({ $secondary }) => ($secondary ? '#2b88d9' : '#fff')} !important;
    border: 1px solid ${({ $secondary }) => ($secondary ? '#2b88d9' : 'transparent')};
    box-shadow: ${({ $secondary }) => ($secondary ? 'none' : '0 2px 10px rgba(43, 136, 217, 0.3)')};

    &:hover {
        background: ${({ $secondary }) => ($secondary ? 'rgba(43, 136, 217, 0.06)' : '#3a96e8')};
        color: ${({ $secondary }) => ($secondary ? '#2b88d9' : '#fff')} !important;
        box-shadow: ${({ $secondary }) => ($secondary ? 'none' : '0 4px 16px rgba(43, 136, 217, 0.4)')};
    }
`;

const ActionButtonElement = styled.button`
    ${actionStyles}
    background: #2b88d9;
    color: #fff !important;
    border: 1px solid transparent;
    box-shadow: 0 2px 10px rgba(43, 136, 217, 0.3);

    &:hover {
        background: #3a96e8;
        color: #fff !important;
        box-shadow: 0 4px 16px rgba(43, 136, 217, 0.4);
    }
`;

const DisabledAction = styled.span`
    ${actionStyles}
    background: rgba(0, 0, 0, 0.06);
    color: #777 !important;
    border: 1px solid transparent;
    cursor: default;

    &:hover {
        transform: none;
    }
`;

const DownloadButtonBase = `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 44px;
    padding: 0 24px;
    border-radius: 9px;
    font-size: 0.94rem;
    font-weight: 700;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    transition: transform 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;

    &:hover {
        transform: translateY(-1px);
    }
`;

const DownloadButton = styled.button`
    ${DownloadButtonBase}
    min-width: ${({ $compact }) => ($compact ? '150px' : '260px')};
    min-height: ${({ $compact }) => ($compact ? '38px' : '48px')};
    padding: ${({ $compact }) => ($compact ? '0 16px' : '0 24px')};
    font-size: ${({ $compact }) => ($compact ? '0.84rem' : '0.94rem')};
    background: ${({ $secondary }) => ($secondary ? 'transparent' : '#111827')};
    color: ${({ $secondary }) => ($secondary ? '#1f2937' : '#fff')} !important;
    border: 1px solid ${({ $secondary }) => ($secondary ? 'rgba(17, 24, 39, 0.22)' : '#111827')};

    &:hover {
        background: ${({ $secondary }) => ($secondary ? 'rgba(17, 24, 39, 0.06)' : '#0f172a')};
    }
`;

const DownloadButtonAnchor = styled.a`
    ${DownloadButtonBase}
    min-width: ${({ $compact }) => ($compact ? '150px' : '260px')};
    min-height: ${({ $compact }) => ($compact ? '38px' : '48px')};
    padding: ${({ $compact }) => ($compact ? '0 16px' : '0 24px')};
    font-size: ${({ $compact }) => ($compact ? '0.84rem' : '0.94rem')};
    background: ${({ $secondary }) => ($secondary ? 'transparent' : '#111827')};
    color: ${({ $secondary }) => ($secondary ? '#1f2937' : '#fff')} !important;
    border: 1px solid ${({ $secondary }) => ($secondary ? 'rgba(17, 24, 39, 0.22)' : '#111827')};

    &:hover {
        background: ${({ $secondary }) => ($secondary ? 'rgba(17, 24, 39, 0.06)' : '#0f172a')};
        color: ${({ $secondary }) => ($secondary ? '#1f2937' : '#fff')} !important;
    }
`;

const DownloadButtonDisabled = styled.span`
    ${DownloadButtonBase}
    min-width: ${({ $compact }) => ($compact ? '150px' : '260px')};
    min-height: ${({ $compact }) => ($compact ? '38px' : '48px')};
    padding: ${({ $compact }) => ($compact ? '0 16px' : '0 24px')};
    font-size: ${({ $compact }) => ($compact ? '0.84rem' : '0.94rem')};
    background: rgba(17, 24, 39, 0.06);
    border: 1px solid transparent;
    color: #8a8f98 !important;
    cursor: default;

    &:hover {
        transform: none;
    }
`;

const ContentSection = styled.section`
    padding: ${({ $editorial }) => ($editorial ? '72px 0' : '96px 0')};
    border-bottom: 0;
    border-top: 1px solid ${({ $editorial }) => ($editorial ? 'rgba(232, 229, 222, 0.72)' : 'rgba(0, 0, 0, 0.06)')};

    &:first-of-type {
        border-top: ${({ $editorial }) => ($editorial ? '0' : '1px solid rgba(0, 0, 0, 0.06)')};
    }

    ${({ $tinted, $editorial }) => $tinted && !$editorial && `
        background: #fafafa;
        margin-left: calc(50% - 50vw);
        margin-right: calc(50% - 50vw);
        padding-left: calc(50vw - 50%);
        padding-right: calc(50vw - 50%);
    `}
`;

const SectionHeading = styled.h2`
    margin: 0 0 ${({ $editorial }) => ($editorial ? '22px' : '48px')};
    font-family: ${({ $editorial }) => ($editorial ? "Georgia, 'Iowan Old Style', 'Palatino Linotype', 'Book Antiqua', Palatino, serif" : 'inherit')};
    font-size: ${({ $editorial }) => ($editorial ? 'clamp(38px, 5vw, 52px)' : 'clamp(1.8rem, 3vw, 2.6rem)')};
    font-weight: ${({ $editorial }) => ($editorial ? '500' : 'inherit')};
    line-height: ${({ $editorial }) => ($editorial ? '1.08' : '1.15')};
    letter-spacing: 0;
    color: #1a1a1a;
`;

const SectionDescription = styled.p`
    margin: 0 0 ${({ $editorial }) => ($editorial ? '24px' : '32px')};
    font-size: ${({ $editorial }) => ($editorial ? '18px' : '1.05rem')};
    line-height: ${({ $editorial }) => ($editorial ? '1.62' : '1.7')};
    color: ${({ $editorial }) => ($editorial ? 'rgba(43, 47, 54, 0.74)' : '#666')};
    max-width: ${({ $editorial }) => ($editorial ? '720px' : '42rem')};
`;

const Paragraph = styled.p`
    margin: 0 0 18px;
    font-size: ${({ $editorial }) => ($editorial ? '18px' : '1rem')};
    line-height: ${({ $editorial }) => ($editorial ? '1.72' : '1.7')};
    color: ${({ $editorial }) => ($editorial ? 'rgba(43, 47, 54, 0.82)' : '#555')};
    max-width: ${({ $editorial }) => ($editorial ? '720px' : '42rem')};
`;

const EditorialList = styled.div`
    max-width: 720px;
    border-top: 1px solid rgba(20, 17, 15, 0.12);
`;

const EditorialListItem = styled.article`
    display: grid;
    grid-template-columns: minmax(150px, 0.72fr) minmax(0, 1.7fr);
    gap: 28px;
    padding: 24px 0;
    border-bottom: 1px solid rgba(20, 17, 15, 0.12);

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
        gap: 8px;
        padding: 20px 0;
    }
`;

const EditorialListTitle = styled.h3`
    margin: 0;
    font-size: 1rem;
    line-height: 1.45;
    color: #1a1a1a;
`;

const EditorialListBody = styled.p`
    margin: 0;
    font-size: 1rem;
    line-height: 1.7;
    color: #555;
`;

const EditorialSteps = styled.ol`
    display: grid;
    gap: 0;
    max-width: 720px;
    margin: 0;
    padding: 0;
    list-style: none;
    border-top: 1px solid rgba(20, 17, 15, 0.12);
`;

const EditorialStep = styled.li`
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr);
    gap: 18px;
    padding: 24px 0;
    border-bottom: 1px solid rgba(20, 17, 15, 0.12);
`;

const EditorialStepNumber = styled.span`
    display: inline-flex;
    width: 30px;
    height: 30px;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(20, 17, 15, 0.22);
    border-radius: 50%;
    color: #1a1a1a;
    font-size: 0.84rem;
    font-weight: 700;
    line-height: 1;
`;

const EditorialStepCopy = styled.div`
    min-width: 0;
`;

const EditorialStepTitle = styled.h3`
    margin: 2px 0 8px;
    color: #1a1a1a;
    font-size: 1.05rem;
    line-height: 1.4;
`;

const EditorialStepBody = styled.p`
    margin: 0;
    color: #555;
    font-size: 1rem;
    line-height: 1.7;
`;

const EditorialStepLink = styled(Link)`
    display: inline-flex;
    margin-top: 10px;
    font-size: 0.95rem;
    font-weight: 600;
    text-decoration: none;

    && {
        color: #2b88d9;
    }

    &&:hover {
        color: #1a6fb8;
        text-decoration: underline;
    }
`;

const FeatureStrip = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    max-width: 720px;
`;

const FeatureChip = styled.span`
    padding: 9px 13px;
    border: 1px solid rgba(20, 17, 15, 0.12);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.65);
    color: #35312e;
    font-size: 0.9rem;
    font-weight: 600;
    line-height: 1.2;
`;

const CardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(${({ $columns = 2 }) => $columns}, minmax(0, 1fr));
    gap: ${({ $editorial }) => ($editorial ? '18px' : '20px 24px')};

    @media (max-width: 960px) {
        grid-template-columns: ${({ $editorial, $columns = 2 }) => ($editorial ? `repeat(${$columns}, minmax(0, 1fr))` : 'repeat(2, minmax(0, 1fr))')};
    }

    @media (max-width: 820px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const RankedList = styled.div`
    display: grid;
    gap: 14px;
    margin-top: 8px;
    max-width: 760px;
`;

const RankedItem = styled.article`
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr);
    gap: 12px;
    align-items: start;
`;

const RankBadge = styled.span`
    width: 26px;
    height: 26px;
    border-radius: 7px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
    background: ${({ $primary }) => ($primary ? '#2b88d9' : '#171714')};
    color: #fff;
    font-size: 0.78rem;
    font-weight: 800;
`;

const RankCopy = styled.div`
    font-size: 1rem;
    line-height: 1.62;
    color: #706d66;

    strong {
        color: #1a1a1a;
        font-weight: 800;
        margin-right: 6px;
    }

    span {
        color: inherit;
    }
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;
    margin-top: 10px;

    @media (max-width: 760px) {
        grid-template-columns: 1fr;
    }
`;

const StatCard = styled.article`
    padding: 24px;
    border-radius: 12px;
    border: 1px solid rgba(20, 17, 15, 0.10);
    background: rgba(255, 255, 255, 0.74);
    box-shadow: 0 10px 30px rgba(23, 23, 20, 0.05);
`;

const StatValue = styled.b`
    display: block;
    margin: 0 0 8px;
    color: #1a1a1a;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 28px;
    line-height: 1.1;
`;

const StatLabel = styled.span`
    display: block;
    color: #706d66;
    font-size: 0.95rem;
    line-height: 1.5;
`;

const AppTableWrap = styled.div`
    background: rgba(255, 255, 255, 0.86);
    border: 1px solid rgba(20, 17, 15, 0.10);
    border-radius: 12px;
    box-shadow: 0 16px 40px rgba(23, 23, 20, 0.04);
    overflow: hidden;
`;

const AppComparisonTable = styled.table`
    border-collapse: collapse;
    margin: 0;
    min-width: 860px;
    table-layout: fixed;
    width: 100%;

    th,
    td {
        padding: 15px 16px;
        text-align: left;
        vertical-align: middle;
    }

    th {
        background: rgba(255, 255, 255, 0.82);
        border-bottom: 1px solid rgba(20, 17, 15, 0.08);
        color: rgba(20, 17, 15, 0.46);
        font-size: 0.62rem;
        font-weight: 800;
        letter-spacing: 0.045em;
        text-transform: uppercase;
        white-space: nowrap;
    }

    td {
        border-bottom: 1px solid rgba(20, 17, 15, 0.07);
        color: #2b2b28;
        font-size: 0.84rem;
        line-height: 1.45;
    }

    tr:last-child td {
        border-bottom: 0;
    }

    tbody tr.featured {
        background: rgba(43, 136, 217, 0.07);
    }

    th:first-child,
    td:first-child {
        width: 16%;
    }

    th:nth-child(2),
    td:nth-child(2) {
        width: 20%;
    }
`;

const AppName = styled.strong`
    align-items: center;
    color: #171714;
    display: inline-flex;
    font-weight: 800;
    gap: 8px;
`;

const AppIconBadge = styled.span`
    align-items: center;
    background: ${({ $featured }) => ($featured ? 'rgba(43, 136, 217, 0.12)' : 'rgba(20, 17, 15, 0.05)')};
    border-radius: 8px;
    display: inline-flex;
    flex-shrink: 0;
    height: 28px;
    justify-content: center;
    width: 28px;

    img {
        height: 18px;
        object-fit: contain;
        width: 18px;
    }
`;

const AppTablePositive = styled.span`
    color: #6b9b75;
    font-weight: 800;
`;

const AppTableMuted = styled.span`
    color: rgba(20, 17, 15, 0.38);
`;

const AlternativeCardList = styled.div`
    display: grid;
    gap: 16px;
`;

const AlternativeCard = styled.article`
    align-items: start;
    background: #fff;
    border: 1px solid ${({ $featured }) => ($featured ? 'rgba(43, 136, 217, 0.45)' : 'rgba(20, 17, 15, 0.10)')};
    border-radius: 14px;
    box-shadow: ${({ $featured }) => ($featured ? '0 12px 32px rgba(43, 136, 217, 0.09)' : '0 8px 24px rgba(23, 23, 20, 0.03)')};
    display: grid;
    gap: 20px;
    grid-template-columns: 42px minmax(0, 1fr) 172px;
    padding: 24px 24px;
    transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: ${({ $featured }) => ($featured ? '0 16px 38px rgba(43, 136, 217, 0.14)' : '0 12px 30px rgba(23, 23, 20, 0.06)')};
    }

    @media (max-width: 820px) {
        grid-template-columns: 38px minmax(0, 1fr);
    }
`;

const AlternativeRank = styled.div`
    color: ${({ $primary }) => ($primary ? '#2b88d9' : 'rgba(20, 17, 15, 0.20)')};
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 38px;
    font-weight: ${({ $primary }) => ($primary ? '800' : '500')};
    line-height: 1;
    padding-top: 2px;
`;

const AlternativeMain = styled.div`
    min-width: 0;
`;

const AlternativeTitleRow = styled.div`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 9px;
    margin-bottom: 12px;
`;

const AlternativeAppIconBadge = styled.span`
    align-items: center;
    background: ${({ $featured }) => ($featured ? 'rgba(43, 136, 217, 0.12)' : 'rgba(20, 17, 15, 0.05)')};
    border-radius: 9px;
    display: inline-flex;
    flex-shrink: 0;
    height: 34px;
    justify-content: center;
    width: 34px;

    img {
        height: 20px;
        object-fit: contain;
        width: 20px;
    }
`;

const AlternativeTitle = styled.h3`
    color: #171714;
    font-size: 1.18rem;
    font-weight: 800;
    margin: 0;
`;

const AlternativeBadge = styled.span`
    background: ${({ $featured }) => ($featured ? '#2b88d9' : 'transparent')};
    border: 1px solid ${({ $featured }) => ($featured ? '#2b88d9' : 'rgba(20, 17, 15, 0.12)')};
    border-radius: 999px;
    color: ${({ $featured }) => ($featured ? '#fff' : 'rgba(20, 17, 15, 0.62)')};
    font-size: 0.72rem;
    font-weight: 600;
    line-height: 1;
    padding: 7px 10px;
`;

const HeroAppsCluster = styled.div`
    align-items: center;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(20, 17, 15, 0.08);
    border-radius: 999px;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.04);
    display: inline-flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 22px;
    padding: 6px 14px;
`;

const HeroClusterItem = styled.div`
    align-items: center;
    color: #171714;
    display: inline-flex;
    font-size: 0.84rem;
    font-weight: ${({ $featured }) => ($featured ? '700' : '600')};
    gap: 6px;
`;

const HeroClusterIconWrap = styled.span`
    align-items: center;
    background: ${({ $background }) => $background || 'rgba(0, 0, 0, 0.06)'};
    border-radius: 50%;
    color: ${({ $color }) => $color || 'inherit'};
    display: inline-flex;
    flex-shrink: 0;
    height: 24px;
    justify-content: center;
    width: 24px;

    img {
        height: 16px;
        object-fit: contain;
        width: 16px;
    }
`;

const HeroClusterVs = styled.span`
    color: rgba(20, 17, 15, 0.38);
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    padding: 0 4px;
    text-transform: uppercase;
`;

const AlternativeBody = styled.p`
    color: rgba(43, 47, 54, 0.70);
    font-size: 0.88rem;
    line-height: 1.58;
    margin: 0 0 16px;
    max-width: 760px;
`;

const AlternativeMeta = styled.p`
    color: rgba(20, 17, 15, 0.40);
    font-size: 0.78rem;
    line-height: 1.5;
    margin: 0;

    strong {
        color: rgba(20, 17, 15, 0.62);
        font-weight: 800;
    }
`;

const AlternativeMetaSeparator = styled.span`
    margin: 0 10px;
    color: rgba(20, 17, 15, 0.24);
`;

const AlternativeAside = styled.aside`
    align-self: stretch;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;

    @media (max-width: 820px) {
        align-items: flex-start;
        grid-column: 2;
        text-align: left;
    }
`;

const AlternativePrice = styled.div`
    color: #171714;
    font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 1.32rem;
    font-weight: 700;
    line-height: 1.1;
`;

const AlternativePriceNote = styled.div`
    color: rgba(20, 17, 15, 0.30);
    font-size: 0.74rem;
    font-weight: 400;
    margin-top: 8px;
    max-width: 160px;
`;

const AlternativeCta = styled(Link)`
    align-items: center;
    background: ${({ $featured }) => ($featured ? '#2b88d9' : 'rgba(43, 136, 217, 0.1)')};
    border: ${({ $featured }) => ($featured ? 'none' : '1px solid rgba(43, 136, 217, 0.25)')};
    border-radius: 8px;
    display: inline-flex;
    font-size: 0.82rem;
    font-weight: 800;
    justify-content: center;
    margin-top: auto;
    min-height: 38px;
    min-width: 150px;
    padding: 0 14px;
    text-decoration: none;
    transition: all 0.18s ease;

    && {
        color: ${({ $featured }) => ($featured ? '#fff' : '#2b88d9')};
    }

    &&:hover {
        background: ${({ $featured }) => ($featured ? '#2577be' : 'rgba(43, 136, 217, 0.18)')};
        border-color: ${({ $featured }) => ($featured ? 'none' : 'rgba(43, 136, 217, 0.4)')};
        color: ${({ $featured }) => ($featured ? '#fff' : '#2577be')};
        transform: translateY(-1px);
    }
`;

const AlternativeCtaExternal = styled.a`
    align-items: center;
    background: rgba(43, 136, 217, 0.1);
    border: 1px solid rgba(43, 136, 217, 0.25);
    border-radius: 8px;
    display: inline-flex;
    font-size: 0.82rem;
    font-weight: 800;
    justify-content: center;
    margin-top: auto;
    min-height: 38px;
    min-width: 150px;
    padding: 0 14px;
    text-decoration: none;
    transition: all 0.18s ease;

    && {
        color: #2b88d9;
    }

    &&:hover {
        background: rgba(43, 136, 217, 0.18);
        color: #2577be;
        transform: translateY(-1px);
    }
`;

const InfoCard = styled.article`
    padding: ${({ $editorial }) => ($editorial ? '28px' : '24px 22px')};
    border-radius: ${({ $editorial }) => ($editorial ? '12px' : '14px')};
    border: 1px solid ${({ $editorial }) => ($editorial ? 'rgba(20, 17, 15, 0.10)' : 'rgba(0, 0, 0, 0.08)')};
    background: ${({ $editorial }) => ($editorial ? 'rgba(255, 255, 255, 0.74)' : 'rgba(255, 255, 255, 0.7)')};
    box-shadow: ${({ $editorial }) => ($editorial ? '0 10px 30px rgba(23, 23, 20, 0.05)' : '0 1px 3px rgba(20, 17, 15, 0.04)')};
    transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        background: #ffffff;
        border-color: ${({ $editorial }) => ($editorial ? 'rgba(20, 17, 15, 0.18)' : 'rgba(0, 0, 0, 0.14)')};
        box-shadow: ${({ $editorial }) => ($editorial ? '0 10px 30px rgba(23, 23, 20, 0.05)' : '0 18px 42px rgba(20, 17, 15, 0.12)')};
        transform: ${({ $editorial }) => ($editorial ? 'none' : 'translateY(-2px)')};
    }
`;

const StepNumber = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 9px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    background: #171714;
    color: #fff;
    font-weight: 700;
`;

const ItemIcon = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    background: rgba(43, 136, 217, 0.1);
    color: #2b88d9;
`;

const CardTitle = styled.h3`
    margin: 0 0 12px;
    font-size: 1.02rem;
    color: #1a1a1a;
`;

const CardBody = styled.p`
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.62;
    color: #666;
`;

const TESTIMONIAL_PAPER = '#fcf9f7';
const TESTIMONIAL_PAPER_DEEP = '#f6efe7';

/*
 * The testimonial block deliberately opts out of the section-alternating
 * $tinted background (see `const tinted = index % 2 === 1` above). Because
 * it can land on either a plain or a tinted section, alternating its own
 * background risked landing tint-on-tint (grey #fafafa on grey) and
 * disappearing into its neighbour. Instead it always carries its own warm
 * paper wash, borrowed from the palette used in ComparePage.js.
 *
 * Note: the site body itself already sits on ${TESTIMONIAL_PAPER}
 * (see assets/scss/core/layout/_main.scss), so that tone alone reads as
 * "no background" next to a plain section. The gradient below keeps
 * ${TESTIMONIAL_PAPER_DEEP} — the visibly deeper, warmer tone — at the top
 * and bottom edges (where this block actually touches its neighbours),
 * so the seam is never invisible regardless of which side of the
 * alternation it lands on.
 */
const TestimonialSection = styled.section`
    padding: 88px 0;
    border-bottom: 1px solid rgba(20, 17, 15, 0.08);
    background: linear-gradient(
        180deg,
        ${TESTIMONIAL_PAPER_DEEP} 0%,
        ${TESTIMONIAL_PAPER} 45%,
        ${TESTIMONIAL_PAPER} 55%,
        ${TESTIMONIAL_PAPER_DEEP} 100%
    );
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
    padding-left: calc(50vw - 50%);
    padding-right: calc(50vw - 50%);
`;

const TestimonialGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 40px 48px;
`;

const TestimonialItem = styled.article`
    display: flex;
    flex-direction: column;
`;

const TestimonialQuoteMark = styled.div`
    font-family: Georgia, 'Times New Roman', serif;
    font-size: clamp(3.4rem, 6vw, 4.6rem);
    line-height: 1;
    color: rgba(26, 26, 26, 0.12);
    margin: 0 0 -0.5rem -0.15rem;
    user-select: none;
`;

const TestimonialQuote = styled.p`
    margin: 0 0 24px;
    font-size: clamp(1.2rem, 1.6vw, 1.35rem);
    line-height: 1.6;
    font-weight: 500;
    color: #1a1a1a;
`;

const TestimonialAttribution = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: auto;
`;

const TestimonialAvatar = styled.img`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
`;

const TestimonialAvatarFallback = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: rgba(43, 136, 217, 0.14);
    color: #2b88d9;
    font-size: 0.85rem;
    font-weight: 600;
`;

const TestimonialAuthorMeta = styled.div`
    min-width: 0;
`;

const TestimonialAuthorName = styled.div`
    font-weight: 600;
    font-size: 0.88rem;
    color: #4a4a4a;
    line-height: 1.3;
`;

const TestimonialAuthorLink = styled.a`
    && {
        color: #14110f;
    }

    &&:hover {
        color: #2b88d9;
    }
`;

const TestimonialAuthorRole = styled.div`
    font-size: 0.8rem;
    color: #888;
    margin-top: 2px;
`;

const TableHeaderLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

const TableHeaderIcon = styled.span`
    width: 22px;
    height: 22px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: ${({ $background }) => $background || 'rgba(0, 0, 0, 0.06)'};
    color: ${({ $color }) => $color || '#1a1a1a'};

    img {
        border-radius: 2px;
    }
`;

// Two layers on purpose: combining overflow-x:auto with overflow-y:hidden on
// a single element makes some browsers permanently reserve a horizontal
// scrollbar gutter (even when nothing needs to scroll), which showed up as
// dead whitespace at the bottom/right of every table. Splitting the rounded
// clipping (outer, overflow:hidden, no scrollbar involved) from the
// horizontal scroll (inner, overflow-x:auto only) avoids that reservation.
const TableWrap = styled.div`
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    overflow: hidden;
`;

const TableScroll = styled.div`
    overflow-x: auto;
`;

const ComparisonTable = styled.table`
    width: 100%;
    margin: 0;
    border-collapse: collapse;
    table-layout: fixed;
    min-width: 640px;

    /* Fixed column proportions (rather than per-table auto-sizing) so every
       stacked category table lines up its Feature/product columns at the
       same x-position, instead of each table sizing columns to its own
       row text and drifting out of alignment with its neighbors. */
    th:first-child,
    td:first-child {
        width: 46%;
    }

    th:nth-child(2),
    td:nth-child(2),
    th:nth-child(3),
    td:nth-child(3) {
        width: 27%;
    }

    th,
    td {
        padding: 14px 18px;
        text-align: left;
        overflow-wrap: break-word;
    }

    th {
        font-size: 0.85rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #666;
        font-weight: 600;
        background: #fafafa;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    }

    td {
        font-size: 0.98rem;
        color: #333;
    }

    tbody tr:nth-child(even) {
        background: #fafbfc;
    }

    tbody tr:not(:last-child) td {
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }
`;

const ComparisonBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.85rem;
    font-weight: 600;
`;

const ComparisonYes = styled(ComparisonBadge)`
    color: #16a34a;
    background: rgba(22, 163, 74, 0.1);
`;

const ComparisonNo = styled(ComparisonBadge)`
    color: #9ca3af;
    background: rgba(156, 163, 175, 0.12);
`;

const ComparisonLimited = styled(ComparisonBadge)`
    color: #b45309;
    background: rgba(180, 83, 9, 0.1);
`;

const ComparisonMeta = styled.p`
    margin: 12px 0 0;
    font-size: 0.88rem;
    line-height: 1.5;
    color: rgba(20, 17, 15, 0.62);
`;

const ComparisonMetaSeparator = styled.span`
    padding: 0 8px;
`;

const ComparisonMetaLink = styled.a`
    && {
        color: #14110f;
    }
    text-decoration: underline;
    text-underline-offset: 2px;

    &&:hover {
        color: #2b88d9;
    }
`;

const CategoryTableGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 28px;
`;

const CategoryTableBlock = styled.div``;

const CategoryTableHeading = styled.h3`
    margin: 0 0 12px;
    font-size: 0.95rem;
    font-weight: 700;
    color: #1a1a1a;
    display: flex;
    align-items: center;
    gap: 8px;

    &::before {
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #2b88d9;
        flex-shrink: 0;
    }
`;

const VersusIntro = styled.div`
    display: grid;
    justify-items: center;
    text-align: center;
    margin: 0 auto 44px;
    max-width: 760px;

    ${SectionHeading} {
        margin-bottom: 18px;
    }

    ${SectionDescription} {
        margin-bottom: 0;
        max-width: 700px;
    }
`;

const VersusEyebrow = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 0 14px;
    font-size: 0.82rem;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #6f6a61;
`;

const VersusGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 34px;

    @media (max-width: 960px) {
        grid-template-columns: 1fr;
    }
`;

const VersusColumn = styled.div`
    min-width: 0;
`;

const VersusColumnHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
`;

const VersusColumnIdentity = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
`;

const VersusLogo = styled.img`
    width: 22px;
    height: 22px;
    display: block;
    flex-shrink: 0;
`;

const VersusLogoMark = styled.span`
    width: 22px;
    height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${({ $color }) => $color || '#6b7280'};
    flex-shrink: 0;

    img,
    svg {
        width: 22px;
        height: 22px;
        display: block;
    }
`;

const VersusColumnTitle = styled.h3`
    margin: 0;
    font-size: 1.55rem;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.03em;
    color: #1a1a1a;
`;

const VersusColumnDescription = styled.p`
    margin: 0 0 14px;
    font-size: 1rem;
    line-height: 1.6;
    color: #6c665d;
    max-width: 34rem;
`;

const VersusColumnDivider = styled.div`
    height: 1px;
    background: rgba(26, 26, 26, 0.08);
    margin-bottom: 18px;
`;

const VersusCard = styled.div`
    display: grid;
    gap: 12px;
`;

const VersusList = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const VersusListItem = styled.li`
    display: grid;
    grid-template-columns: 30px minmax(0, 1fr);
    align-items: center;
    gap: 14px;
    font-size: 0.98rem;
    color: #353b45;
    line-height: 1.6;
    padding: 20px 20px 20px 18px;
    border-radius: 22px;
    background: #f7f5f1;
    border: 1px solid rgba(17, 24, 39, 0.07);

    span {
        display: block;
        padding-top: 1px;
    }
`;

const StrengthIconWrap = styled.span`
    width: 30px;
    height: 30px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #eeebe5;
    color: #6d675f;
    flex-shrink: 0;
    line-height: 0;
    position: relative;

    svg {
        width: 15px;
        height: 15px;
        display: block;
        position: absolute;
        top: 50%;
        left: 50%;
        margin: 0;
        transform: translate(-50%, -50%);
        flex: none;
    }
`;

const DownloadGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 16px;
`;

const DownloadCard = styled.article`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 24px;
    background: #f7f8f9;
    border: 1px solid transparent;
    border-radius: 8px;
    transition: border-color 0.2s ease;

    &:hover {
        border-color: rgba(0, 0, 0, 0.12);
    }
`;

const DownloadIconWrap = styled.div`
    width: 56px;
    height: 56px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
    color: ${({ $color }) => $color || '#2b88d9'};
    background: ${({ $background }) => $background || 'rgba(43, 136, 217, 0.10)'};
`;

const DownloadHubSection = styled.section`
    padding: 0 0 96px;
`;

const PrimaryDownloadStack = styled.div`
    display: grid;
    justify-content: center;
    gap: 12px;
    margin: 0 0 48px;

    @media (max-width: 640px) {
        margin-top: 0;

        ${DownloadButton},
        ${DownloadButtonAnchor},
        ${DownloadButtonDisabled} {
            width: 100%;
            min-width: 0;
        }
    }
`;

const DownloadPanelGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 28px;

    @media (max-width: 860px) {
        grid-template-columns: 1fr;
    }
`;

const DownloadPanel = styled.article`
    padding: 28px;
    border-radius: 22px;
    border: 1px solid rgba(17, 24, 39, 0.10);
    background:
        linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(247, 249, 252, 0.96)),
        radial-gradient(circle at 20% 0%, rgba(43, 136, 217, 0.12), transparent 34%);
    box-shadow: 0 18px 60px rgba(17, 24, 39, 0.06);
`;

const DownloadPanelTitle = styled.h2`
    display: inline-flex;
    align-items: center;
    gap: 12px;
    margin: 0 0 22px;
    font-size: 1.15rem;
    color: #1a1a1a;
`;

const DownloadPanelIcon = styled.span`
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 11px;
    background: rgba(43, 136, 217, 0.10);
    color: #2b88d9;
`;

const DownloadList = styled.div`
    display: grid;
    gap: 10px;
`;

const DownloadListItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid rgba(17, 24, 39, 0.07);

    &:last-child {
        border-bottom: none;
    }

    @media (max-width: 560px) {
        align-items: stretch;
        flex-direction: column;
    }
`;

const PlatformSummary = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-width: 140px;
    color: #3f4652;
    font-size: 0.95rem;
    font-weight: 650;
`;

const FaqList = styled.div`
    display: grid;
    gap: 0;
    border-top: 1px solid ${({ $editorial }) => ($editorial ? 'rgba(232, 229, 222, 0.9)' : 'rgba(0, 0, 0, 0.07)')};
    border-bottom: 1px solid ${({ $editorial }) => ($editorial ? 'rgba(232, 229, 222, 0.9)' : 'rgba(0, 0, 0, 0.07)')};
`;

const FaqItem = styled.article`
    padding: 24px 0;
    border-bottom: 1px solid ${({ $editorial }) => ($editorial ? 'rgba(232, 229, 222, 0.9)' : 'rgba(0, 0, 0, 0.07)')};

    &:last-child {
        border-bottom: none;
    }
`;

const FaqQuestion = styled.h3`
    align-items: center;
    color: #1a1a1a;
    cursor: ${({ $editorial }) => ($editorial ? 'pointer' : 'default')};
    display: flex;
    font-family: ${({ $editorial }) => ($editorial ? "Georgia, 'Iowan Old Style', 'Palatino Linotype', 'Book Antiqua', Palatino, serif" : 'inherit')};
    font-size: ${({ $editorial }) => ($editorial ? '1.28rem' : '1.05rem')};
    font-weight: 700;
    justify-content: space-between;
    list-style: none;
    margin: ${({ $editorial }) => ($editorial ? '0' : '0 0 10px')};
    gap: 24px;

    &::-webkit-details-marker {
        display: none;
    }
`;

const FaqAnswer = styled.p`
    margin: 0;
    line-height: 1.7;
    color: #666;

    details & {
        margin-top: 18px;
        max-width: 850px;
    }
`;

const FaqAnswerLink = styled(Link)`
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 0.16em;

    && {
        color: #2b88d9;
    }

    &&:hover {
        color: #1a6fb8;
    }
`;

const FaqToggle = styled.span`
    color: #6f96d4;
    flex-shrink: 0;
    font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 1;

    details[open] & {
        transform: rotate(45deg);
    }
`;

const CompactLinkGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px 16px;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const CompactLinkCard = styled(Link)`
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 18px;
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: #fafafa;
    color: #1a1a1a;
    text-decoration: none;
    transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        background: #ffffff;
        border-color: rgba(0, 0, 0, 0.14);
        box-shadow: 0 18px 42px rgba(20, 17, 15, 0.08);
        transform: translateY(-2px);
    }
`;

const CompactLinkChip = styled.span`
    width: 32px;
    height: 32px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: ${({ $background }) => $background || '#111827'};
    color: ${({ $color }) => $color || '#ffffff'};
`;

const CompactLinkTitle = styled.span`
    font-size: 0.95rem;
    font-weight: 600;
    line-height: 1.4;
    color: #1a1a1a;
`;

const FinalCta = styled.section`
    padding: ${({ $editorial }) => ($editorial ? '64px 32px' : '96px 0 0')};
    margin: ${({ $editorial }) => ($editorial ? '36px 0 88px' : '0')};
    border-radius: ${({ $editorial }) => ($editorial ? '24px' : '0')};
    background: ${({ $editorial }) => ($editorial ? '#1d1b18' : 'transparent')};
    box-shadow: ${({ $editorial }) => ($editorial ? '0 10px 30px rgba(23, 23, 20, 0.05)' : 'none')};
    color: ${({ $editorial }) => ($editorial ? '#fff' : 'inherit')};
    text-align: ${({ $editorial }) => ($editorial ? 'center' : 'left')};

    ${SectionHeading} {
        color: ${({ $editorial }) => ($editorial ? '#fff' : '#1a1a1a')};
        margin-left: ${({ $editorial }) => ($editorial ? 'auto' : '0')};
        margin-right: ${({ $editorial }) => ($editorial ? 'auto' : '0')};
        max-width: ${({ $editorial }) => ($editorial ? '850px' : 'none')};
    }

    ${SectionDescription} {
        color: ${({ $editorial }) => ($editorial ? '#aaa69f' : '#666')};
        margin-left: ${({ $editorial }) => ($editorial ? 'auto' : '0')};
        margin-right: ${({ $editorial }) => ($editorial ? 'auto' : '0')};
        max-width: ${({ $editorial }) => ($editorial ? '650px' : '42rem')};
    }
`;

export default MarketingPage;
